import { Component, computed, inject, signal } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { CreateAnalysisConfigurationDto } from '@api-types/dtos/create-analysis-configuration.dto';
import { AnalysisType } from '@api-types/enums/analysis-type.enum';

import { DialogOptionalCtaType, PmDialogComponent } from '@libs/ui';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AnalysisMutationsService } from 'demand-forecasting/analysis/services/analysis-mutations.service';
import { selectConfigurationId } from 'demand-forecasting/store/router-state/router.selectors';
import { ErrorMeasuresStepAnalysisConfigurationComponent } from './steps/error-measures-step-analysis-configuration/error-measures-step-analysis-configuration.component';
import { GeneralStepAnalysisConfigurationComponent } from './steps/general-step-analysis-configuration/general-step-analysis-configuration.component';
import { HierarchyStepAnalysisConfigurationComponent } from './steps/hierarchy-step-analysis-configuration/hierarchy-step-analysis-configuration.component';
import { SummaryStepAnalysisConfigurationComponent } from './steps/summary-step-analysis-configuration/summary-step-analysis-configuration.component';

type CreateAnalysisConfigurationStepId =
  | 'general'
  | 'hierarchy'
  | 'errorMeasures'
  | 'summary';

type UndefineableCreateAnalysisConfigurationDto = {
  [K in keyof CreateAnalysisConfigurationDto]:
    | CreateAnalysisConfigurationDto[K]
    | undefined;
};

@Component({
  selector: 'pm-create-analysis-configuration-dialog',
  templateUrl: `create-analysis-configuration-dialog.component.html`,
  imports: [
    TranslateModule,
    GeneralStepAnalysisConfigurationComponent,
    HierarchyStepAnalysisConfigurationComponent,
    ErrorMeasuresStepAnalysisConfigurationComponent,
    SummaryStepAnalysisConfigurationComponent,
    PmDialogComponent,
  ],
})
export class CreateAnalysisConfigurationDialogComponent {
  private readonly store = inject(Store);
  protected readonly analysisMutationsService = inject(
    AnalysisMutationsService,
  );

  private readonly translate = inject(TranslateService);

  private readonly configurationId = this.store.selectSignal(
    selectConfigurationId,
  );

  protected readonly dto = signal<UndefineableCreateAnalysisConfigurationDto>({
    general: undefined,
    hierarchy: undefined,
    errorMeasures: undefined,
  });

  protected readonly consensusEvaluated = computed(() => {
    const general = this.dto().general;
    if (!general) {
      return false;
    }
    return general.analysisType === AnalysisType.SINGLE_PERIOD
      ? !!general.consensusCycleId
      : !!general.includeConsensus;
  });

  protected updateDto<K extends keyof CreateAnalysisConfigurationDto>(
    key: K,
    value: CreateAnalysisConfigurationDto[K] | undefined,
  ): void {
    this.dto.update((dto) => ({ ...dto, [key]: value }));
  }

  protected readonly dialogRef = inject(
    MatDialogRef<CreateAnalysisConfigurationDialogComponent>,
  );

  protected readonly steps: CreateAnalysisConfigurationStepId[] = [
    'general',
    'hierarchy',
    'errorMeasures',
    'summary',
  ];

  protected readonly stepId = signal<CreateAnalysisConfigurationStepId>(
    this.steps.length > 0 ? this.steps[0] : 'general',
  );

  protected stepIsValid = signal<boolean>(false);

  protected readonly stepCounts = computed(() => {
    const stepId = this.stepId();
    const matchingStep = this.steps.indexOf(stepId);
    return { current: matchingStep + 1, total: this.steps.length };
  });

  protected backIsPossible = computed(() => {
    const stepId = this.stepId();
    const index = this.steps.indexOf(stepId);
    return index > 0;
  });

  protected isLastStep = computed(() => {
    const stepId = this.stepId();
    const index = this.steps.indexOf(stepId);
    return index === this.steps.length - 1;
  });

  protected handleNext(stepId: CreateAnalysisConfigurationStepId) {
    const index = this.steps.indexOf(stepId);
    if (index >= this.steps.length - 1) {
      return this.onSubmit();
    }
    this.stepId.set(this.steps[index + 1]);
  }

  public readonly ctaConfig = computed(() => {
    const stepCounts = this.stepCounts();
    const isLoading =
      this.analysisMutationsService.createAnalysisConfigurationLoading();

    const currentStepId = this.stepId();

    const primaryLabel = this.isLastStep()
      ? 'ANALYSIS.CREATE_CONFIGURATION.FINISH_AND_CALCULATE'
      : 'UTILITY.NEXT';

    return {
      optional: {
        type: 'text',
        cssClass: 'text-content-default text-sm',
        value: this.translate.instant('UTILITY.STEP_X_OF_Y', stepCounts),
      } as DialogOptionalCtaType,
      secondary: {
        disabled: isLoading,
        text: this.backIsPossible() ? 'UTILITY.BACK' : 'UTILITY.CANCEL',
        handleClick: () =>
          this.backIsPossible()
            ? this.handleBack(currentStepId)
            : this.dialogRef.close(),
      },
      primary: {
        text: primaryLabel,
        handleClick: () => this.handleNext(currentStepId),
        disabled: !this.stepIsValid(),
        isLoading: isLoading,
      },
    };
  });

  protected handleBack(stepId: CreateAnalysisConfigurationStepId) {
    const index = this.steps.indexOf(stepId);
    if (index <= 0) return;
    this.stepId.set(this.steps[index - 1]);
  }

  private onSubmit(): void {
    const configurationId = this.configurationId();
    if (!configurationId) {
      console.error(
        'Cannot submit analysis configuration: configurationId is undefined',
      );
      return;
    }

    const isFullDto = (
      dto: UndefineableCreateAnalysisConfigurationDto,
    ): dto is CreateAnalysisConfigurationDto =>
      Object.values(dto).every((v) => v !== undefined);

    const dto = this.dto();
    if (!isFullDto(dto)) {
      console.error('DTO is incomplete:', dto);
      return;
    }

    this.analysisMutationsService
      .createAnalysisConfiguration(configurationId, dto)
      .subscribe(() => this.dialogRef.close());
  }
}
